import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { Button, Input, SpecraCheckbox, SpecraFormField, SpecraSelect, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/authContext';
import { deliveryClient, morgansClient, projectDataClient } from '../../../lib/connect/clients';
const T = Testids.opportunityContext;
const unavailable = (error)=>[
        Code.PermissionDenied,
        Code.NotFound,
        Code.Unauthenticated
    ].includes(ConnectError.from(error).code);
export function OpportunityContextEntry() {
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const id = params.get('contextOpportunity') ?? '';
    const list = useQuery({
        queryKey: [
            'opportunityContextList',
            user?.tenant_id,
            user?.id
        ],
        queryFn: async ()=>(await morgansClient.listCanonicalOpportunities({})).opportunities,
        enabled: !!user,
        retry: false,
        gcTime: 0
    });
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3 my-6",
        "aria-label": "Opportunity context",
        children: [
            /*#__PURE__*/ _jsx("h2", {
                className: "font-semibold",
                children: "Customer & contracting office"
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Opportunity",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "aria-label": "Opportunity context",
                    value: id,
                    onChange: (e)=>setParams((p)=>{
                            p.set('contextOpportunity', e.target.value);
                            p.delete('contextAssessment');
                            return p;
                        }),
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "Choose an Opportunity…"
                        }),
                        !list.error && list.data?.map((o)=>/*#__PURE__*/ _jsx("option", {
                                value: o.id,
                                children: o.title
                            }, o.id))
                    ]
                })
            }),
            list.isLoading && /*#__PURE__*/ _jsx("output", {
                children: "Loading Opportunities…"
            }),
            list.error && /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Opportunities unavailable.",
                    ' ',
                    /*#__PURE__*/ _jsx(Button, {
                        variant: "outline",
                        onClick: ()=>void list.refetch(),
                        children: "Retry Opportunities"
                    })
                ]
            }),
            !id && !list.isLoading && !list.error && /*#__PURE__*/ _jsx("p", {
                children: "Choose a business Opportunity, including one created without a Notice, to record its organizations and market context."
            }),
            id && user && /*#__PURE__*/ _jsx(ContextLoader, {
                id: id
            }, `${user.tenant_id}:${user.id}:${id}:${params.get('contextAssessment') ?? ''}`)
        ]
    });
}
function ContextLoader({ id }) {
    const { user } = useAuth();
    const [params] = useSearchParams();
    const assessmentId = params.get('contextAssessment') ?? '';
    const query = useQuery({
        queryKey: [
            'opportunityContext',
            user?.tenant_id,
            user?.id,
            id,
            assessmentId
        ],
        retry: false,
        gcTime: 0,
        refetchOnMount: 'always',
        queryFn: async ()=>{
            const context = (await projectDataClient.getOpportunityContext({
                opportunityId: id
            })).context;
            if (!context) throw new Error('Context unavailable');
            const [orgs, companies, profiles, coverage, saved] = await Promise.all([
                deliveryClient.listGovernmentOrganizations({}),
                deliveryClient.listCompanyProfiles({}),
                morgansClient.listTrackingProfiles({}),
                morgansClient.listNoticeSourceCoverage({
                    opportunityId: id
                }),
                assessmentId ? projectDataClient.getOpportunityWhitespaceAssessment({
                    opportunityId: id,
                    assessmentId
                }) : Promise.resolve(undefined)
            ]);
            return {
                context,
                orgs: orgs.organizations,
                companies: companies.profiles,
                profiles: profiles.profiles,
                coverage: coverage.coverage,
                saved: saved?.assessment
            };
        }
    });
    if (query.error) return /*#__PURE__*/ _jsxs("div", {
        "data-testid": T.unavailable,
        role: "alert",
        children: [
            "Context unavailable.",
            ' ',
            /*#__PURE__*/ _jsx(Button, {
                variant: "outline",
                onClick: ()=>void query.refetch(),
                children: "Retry context"
            })
        ]
    });
    if (!query.data || query.isFetching) return /*#__PURE__*/ _jsx("output", {
        "data-testid": T.loading,
        children: "Loading customer and market context…"
    });
    return /*#__PURE__*/ _jsx(ContextEditor, {
        id: id,
        data: query.data
    });
}
function ContextEditor({ id, data }) {
    const { user } = useAuth();
    const [, setParams] = useSearchParams();
    const [customer, setCustomer] = useState({
        organizationId: data.context.customer?.organizationId ?? '',
        sourceName: data.context.customer?.sourceName ?? '',
        manualContext: data.context.customer?.manualContext ?? '',
        sourceReferenceIds: data.context.customer?.sourceReferenceIds ?? []
    });
    const [contracting, setContracting] = useState({
        organizationId: data.context.contracting?.organizationId ?? '',
        sourceName: data.context.contracting?.sourceName ?? '',
        manualContext: data.context.contracting?.manualContext ?? '',
        sourceReferenceIds: data.context.contracting?.sourceReferenceIds ?? []
    });
    const [base, setBase] = useState(data.context);
    const [confirm, setConfirm] = useState(false);
    const [denied, setDenied] = useState(false);
    const [message, setMessage] = useState('');
    const [savedContext, setSavedContext] = useState(false);
    const [companyId, setCompany] = useState(data.saved?.companyId ?? '');
    const [profileId, setProfile] = useState(data.saved?.trackingProfileId ?? '');
    const [companySources, setCompanySources] = useState(data.saved?.companySourceReferenceIds ?? []);
    const [marketSources, setMarketSources] = useState(data.saved?.marketSourceReferenceIds ?? []);
    const [coverageIds, setCoverage] = useState(data.saved?.coverageIds ?? []);
    const [decision, setDecision] = useState(data.saved?.gapDecision ?? 'undecided');
    const [reason, setReason] = useState(data.saved?.decisionReason ?? '');
    const [limitations, setLimitations] = useState(data.saved?.limitations ?? '');
    const evidence = useQuery({
        queryKey: [
            'opportunityContextEvidence',
            user?.tenant_id,
            user?.id,
            id,
            profileId,
            companyId
        ],
        queryFn: ()=>projectDataClient.listOpportunityContextEvidence({
                opportunityId: id,
                companyId
            }),
        retry: false,
        gcTime: 0
    });
    const fail = (error)=>{
        if (unavailable(error)) setDenied(true);
        else setMessage(ConnectError.from(error).code === Code.Aborted ? 'This context changed. Refresh the saved version, review your draft and save again.' : 'Could not save. Your draft is retained; retry when ready.');
    };
    const save = useMutation({
        mutationFn: ()=>projectDataClient.updateOpportunityContext({
                opportunityId: id,
                customer,
                contracting,
                confirmCustomer: confirm,
                expectedVersion: base.version
            }),
        onSuccess: (r)=>{
            if (r.context) setBase(r.context);
            setConfirm(false);
            setMessage('');
            setSavedContext(true);
        },
        onError: fail
    });
    const refresh = useMutation({
        mutationFn: ()=>projectDataClient.getOpportunityContext({
                opportunityId: id
            }),
        onSuccess: (r)=>{
            if (r.context) setBase(r.context);
            setMessage('Saved version refreshed. Your draft is retained; review both organizations before saving.');
        },
        onError: fail
    });
    const record = useMutation({
        mutationFn: ()=>projectDataClient.recordOpportunityWhitespaceAssessment({
                opportunityId: id,
                trackingProfileId: profileId,
                companyId,
                companySourceReferenceIds: companySources,
                marketSourceReferenceIds: marketSources,
                coverageIds,
                limitations: Array.from(new Set([
                    ...limitations.split('\n'),
                    ...selectedCoverage.map((c)=>c.detail)
                ].filter(Boolean))).join('\n'),
                gapDecision: decision,
                decisionReason: reason
            }),
        onSuccess: (r)=>{
            const assessmentId = r.assessment?.id;
            if (assessmentId) setParams((p)=>{
                p.set('contextAssessment', assessmentId);
                return p;
            }, {
                replace: true
            });
        },
        onError: fail
    });
    if (denied || evidence.error && unavailable(evidence.error)) return /*#__PURE__*/ _jsx("div", {
        "data-testid": T.unavailable,
        role: "alert",
        children: "Context unavailable."
    });
    const market = evidence.error ? [] : evidence.data?.market ?? [];
    const company = evidence.error ? [] : evidence.data?.company ?? [];
    const profile = data.profiles.find((p)=>p.id === profileId);
    const selectedCoverage = data.coverage.filter((c)=>coverageIds.includes(c.id));
    const selectedCompany = company.filter((s)=>companySources.includes(s.id));
    const selectedMarket = market.filter((s)=>marketSources.includes(s.id));
    const needsConfirmation = !!customer.organizationId && (customer.organizationId !== base.customer?.organizationId || !base.customerConfirmedBy);
    const busy = save.isPending || refresh.isPending || record.isPending;
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": T.panel,
        className: "border rounded p-4 space-y-4",
        children: [
            /*#__PURE__*/ _jsx("p", {
                children: "Public procurement evidence does not confirm the operational customer. Both roles may remain unresolved."
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "grid gap-4 md:grid-cols-2",
                children: [
                    {
                        title: 'Customer / mission office',
                        value: customer,
                        set: setCustomer,
                        org: T.customer,
                        name: T.unresolvedName,
                        manual: T.manualContext,
                        field: 'customer_org_id'
                    },
                    {
                        title: 'Contracting office',
                        value: contracting,
                        set: setContracting,
                        org: T.contracting,
                        name: T.contractingSourceName,
                        manual: T.contractingManualContext,
                        field: 'contracting_org_id'
                    }
                ].map((role)=>/*#__PURE__*/ _jsxs("fieldset", {
                        className: "space-y-2",
                        disabled: busy,
                        children: [
                            /*#__PURE__*/ _jsx("legend", {
                                className: "font-semibold",
                                children: role.title
                            }),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Government organization",
                                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                    "aria-label": role.title,
                                    "data-testid": role.org,
                                    value: role.value.organizationId,
                                    onChange: (e)=>{
                                        role.set({
                                            ...role.value,
                                            organizationId: e.target.value
                                        });
                                        setSavedContext(false);
                                        if (role.field === 'customer_org_id') setConfirm(false);
                                    },
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "",
                                            children: "Unknown / unresolved"
                                        }),
                                        data.orgs.map((o)=>/*#__PURE__*/ _jsx("option", {
                                                value: o.id,
                                                children: o.name
                                            }, o.id))
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Source name",
                                children: /*#__PURE__*/ _jsx(Input, {
                                    "data-testid": role.name,
                                    value: role.value.sourceName,
                                    onChange: (e)=>role.set({
                                            ...role.value,
                                            sourceName: e.target.value
                                        })
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Manual context",
                                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                    "data-testid": role.manual,
                                    value: role.value.manualContext,
                                    onChange: (e)=>role.set({
                                            ...role.value,
                                            manualContext: e.target.value
                                        })
                                })
                            }),
                            /*#__PURE__*/ _jsx(EvidencePicker, {
                                label: `${role.title} source lineage`,
                                rows: market.filter((s)=>s.targetField === role.field),
                                value: role.value.sourceReferenceIds,
                                onChange: (v)=>role.set({
                                        ...role.value,
                                        sourceReferenceIds: v
                                    })
                            })
                        ]
                    }, role.field))
            }),
            /*#__PURE__*/ _jsx("p", {
                "data-testid": T.customerState,
                children: base.customerConfirmedBy && customer.organizationId === base.customer?.organizationId ? 'Confirmed operational customer' : 'Unresolved — requires explicit confirmation'
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Confirm operational customer",
                children: /*#__PURE__*/ _jsx(SpecraCheckbox, {
                    "data-testid": T.confirmCustomer,
                    checked: confirm,
                    disabled: busy,
                    onCheckedChange: (v)=>setConfirm(v === true)
                })
            }),
            /*#__PURE__*/ _jsx(Button, {
                "data-testid": T.save,
                disabled: busy || needsConfirmation && !confirm,
                onClick: ()=>save.mutate(),
                children: "Save context"
            }),
            save.isPending && /*#__PURE__*/ _jsx("output", {
                children: "Saving context…"
            }),
            savedContext && /*#__PURE__*/ _jsx("output", {
                "data-testid": T.saved,
                children: "Context saved"
            }),
            message && /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                children: [
                    message,
                    ' ',
                    /*#__PURE__*/ _jsx(Button, {
                        variant: "outline",
                        disabled: busy,
                        onClick: ()=>refresh.mutate(),
                        children: "Refresh saved version"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("section", {
                "data-testid": T.whitespace,
                className: "space-y-3 border-t pt-4",
                "aria-label": "Whitespace context",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "font-semibold",
                        children: "Why this may be a gap"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        children: "Use a Profile's stated direction, permitted evidence and your rationale to assess relevance. No recorded work in the available evidence does not mean the company never worked here."
                    }),
                    /*#__PURE__*/ _jsxs("fieldset", {
                        disabled: busy,
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Tracking Profile",
                                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                    "data-testid": T.profile,
                                    value: profileId,
                                    onChange: (e)=>setProfile(e.target.value),
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "",
                                            children: "Choose a Profile…"
                                        }),
                                        data.profiles.map((p)=>/*#__PURE__*/ _jsx("option", {
                                                value: p.id,
                                                children: p.name
                                            }, p.id))
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                children: [
                                    /*#__PURE__*/ _jsx(Link, {
                                        to: "/opportunities/tracking-profiles/create",
                                        children: "Create Profile"
                                    }),
                                    profileId && /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            ' ',
                                            "·",
                                            ' ',
                                            /*#__PURE__*/ _jsx(Link, {
                                                to: `/opportunities/tracking-profiles/${profileId}/edit`,
                                                children: "Refine Profile"
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                "data-testid": T.intent,
                                children: [
                                    profile?.description,
                                    profile?.filters?.keywords.length ? ` — Keywords: ${profile.filters.keywords.join(', ')}` : ''
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Company",
                                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                    "data-testid": T.company,
                                    value: companyId,
                                    onChange: (e)=>{
                                        setCompany(e.target.value);
                                        setCompanySources([]);
                                    },
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "",
                                            children: "Choose a company…"
                                        }),
                                        data.companies.map((c)=>/*#__PURE__*/ _jsx("option", {
                                                value: c.id,
                                                children: c.name
                                            }, c.id))
                                    ]
                                })
                            }),
                            evidence.isFetching && /*#__PURE__*/ _jsx("output", {
                                children: "Loading authorized evidence…"
                            }),
                            evidence.error && /*#__PURE__*/ _jsxs("p", {
                                role: "alert",
                                children: [
                                    "Evidence unavailable.",
                                    ' ',
                                    /*#__PURE__*/ _jsx(Button, {
                                        onClick: ()=>void evidence.refetch(),
                                        children: "Retry evidence"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(EvidencePicker, {
                                label: "Company evidence",
                                testid: T.companySource,
                                rows: company,
                                value: companySources,
                                onChange: setCompanySources
                            }),
                            /*#__PURE__*/ _jsx(EvidenceText, {
                                testid: T.companyEvidence,
                                rows: selectedCompany
                            }),
                            /*#__PURE__*/ _jsx(EvidencePicker, {
                                label: "Market evidence",
                                testid: T.marketSource,
                                rows: market,
                                value: marketSources,
                                onChange: setMarketSources
                            }),
                            /*#__PURE__*/ _jsx(EvidenceText, {
                                testid: T.marketEvidence,
                                rows: selectedMarket
                            }),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Source coverage",
                                children: /*#__PURE__*/ _jsx(SpecraSelect, {
                                    multiple: true,
                                    "data-testid": T.coverageSource,
                                    value: coverageIds,
                                    onChange: (e)=>setCoverage(Array.from(e.target.selectedOptions, (o)=>o.value)),
                                    children: data.coverage.map((c)=>/*#__PURE__*/ _jsxs("option", {
                                            value: c.id,
                                            children: [
                                                c.source,
                                                " — ",
                                                c.state,
                                                ": ",
                                                c.detail
                                            ]
                                        }, c.id))
                                })
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                "data-testid": T.coverage,
                                children: [
                                    selectedCoverage.length ? selectedCoverage.map((c)=>`${c.state === 'partial' ? 'Partial' : c.state} — ${c.source}: ${c.detail}`).join('\n') : 'Coverage unknown — select available source coverage.',
                                    ' ',
                                    "Company history and market coverage are incomplete or unknown."
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                "data-testid": T.limitations,
                                children: [
                                    selectedCoverage.map((c)=>/*#__PURE__*/ _jsx("p", {
                                            children: c.detail
                                        }, c.id)),
                                    /*#__PURE__*/ _jsx(SpecraFormField, {
                                        label: "Additional limitations",
                                        children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                            value: limitations,
                                            onChange: (e)=>setLimitations(e.target.value)
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                "data-testid": T.whitespaceState,
                                children: !data.saved || data.saved.state === 'candidate_gap' ? 'Candidate gap — not confirmed whitespace' : 'Assessment unavailable'
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                children: data.saved?.explanation
                            }),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Does this gap matter?",
                                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                    "data-testid": T.gapDecision,
                                    value: decision,
                                    onChange: (e)=>setDecision(e.target.value),
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "undecided",
                                            children: "Undecided"
                                        }),
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "matters",
                                            children: "Matters"
                                        }),
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "does_not_matter",
                                            children: "Does not matter"
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Why does the selected evidence fit the Profile direction?",
                                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                    "data-testid": T.decisionReason,
                                    value: reason,
                                    onChange: (e)=>setReason(e.target.value)
                                })
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                children: "Your rationale connects the selected sources to the Profile intent. It is a human assessment, not verified strategy or complete history."
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                "data-testid": T.recordAssessment,
                                disabled: busy || !profileId || !companyId || !reason.trim() || evidence.isFetching || !!evidence.error,
                                onClick: ()=>record.mutate(),
                                children: "Record assessment"
                            })
                        ]
                    }),
                    record.isPending && /*#__PURE__*/ _jsx("output", {
                        children: "Recording assessment…"
                    }),
                    data.saved && /*#__PURE__*/ _jsx("output", {
                        "data-testid": T.assessmentSaved,
                        children: "Assessment saved"
                    })
                ]
            })
        ]
    });
}
function EvidencePicker({ label, testid, rows, value, onChange }) {
    return /*#__PURE__*/ _jsxs(SpecraFormField, {
        label: label,
        children: [
            /*#__PURE__*/ _jsx(SpecraSelect, {
                multiple: true,
                "data-testid": testid,
                value: value,
                onChange: (e)=>onChange(Array.from(e.target.selectedOptions, (o)=>o.value)),
                children: rows.map((s)=>/*#__PURE__*/ _jsxs("option", {
                        value: s.id,
                        children: [
                            s.sourceKind || 'Source',
                            ": ",
                            s.quote || 'No quoted text',
                            s.sourceSection ? ` — ${s.sourceSection}` : '',
                            s.sourcePage ? ` (page ${s.sourcePage})` : ''
                        ]
                    }, s.id))
            }),
            rows.length === 0 && /*#__PURE__*/ _jsx("span", {
                children: "No available evidence for this selection. Continue with manual context or refine the Profile."
            })
        ]
    });
}
function EvidenceText({ rows, testid }) {
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": testid,
        children: rows.map((s)=>/*#__PURE__*/ _jsxs("blockquote", {
                children: [
                    s.quote,
                    ' ',
                    /*#__PURE__*/ _jsxs("cite", {
                        children: [
                            s.sourceKind || 'Source',
                            s.sourceSection && ` · ${s.sourceSection}`,
                            s.sourcePage > 0 && ` · page ${s.sourcePage}`
                        ]
                    })
                ]
            }, s.id))
    });
}
